import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PencilAltIcon,
  TrashIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  RefreshIcon
} from '@heroicons/react/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Pagination from './Pagination';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import axios from 'axios';
import { toast } from 'react-toastify';
import { changeExchangeStatus, deleteExchange } from '../controller/exchange-controller';
import { Dialog, Transition as HeadlessTransition } from '@headlessui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import Modal from '../../../../components/Modal'; // Adjust the path as needed to point to your Modal component
import { usePermissionViewModel } from '../../permissions/viewmodel/permission-viewmodel';
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const ExchangesList = ({
  exchanges,
  pagination,
  onPageChange,
  onPageSizeChange,
  isLoading,
  selectedExchanges,
  handleExchangeSelection,
  refreshData,
  refreshDashboard
}) => {
  const [changingStatus, setChangingStatus] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [exchangeToDelete, setExchangeToDelete] = useState(null);
  const {hasPermission} = usePermissionViewModel('echange')

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch (error) {
      console.error('Date formatting error:', error);
      return dateString;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'En attente': 'bg-yellow-100 text-yellow-800',
      'Retour reçu': 'bg-blue-100 text-blue-800',
      'Confirmé': 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get available next statuses based on current status
  const getAvailableStatuses = (currentStatus) => {
    switch (currentStatus) {
      case 'En attente':
        return ['Retour reçu'];
      case 'Retour reçu':
        return [];
      case 'Confirmé':
        return ['Retour reçu'];
      default:
        return [];
    }
  };

  // Map status names to API values
  const statusToApiValue = {
    'En attente': 'pending',
    'Retour reçu': 'returnReceived',
    'Confirmé': 'confirmed',
  };

  // Map API values to status names
  const apiValueToStatus = {
    'pending': 'En attente',
    'returnReceived': 'Retour reçu',
    'confirmed': 'Confirmé',
  };

  // Translate API status to display status
  const translateStatus = (status) => {
    return apiValueToStatus[status] || status;
  };

  // Handle status change
  const handleStatusChange = async (exchangeId, newStatus) => {
    try {
      setChangingStatus(exchangeId);
      const apiStatus = statusToApiValue[newStatus];
      await changeExchangeStatus(exchangeId, apiStatus);
      toast.success(`Statut changé en "${newStatus}"`);

      // Refresh data after status change
      if (refreshData) {
        refreshData();
      }
    } catch (error) {
      toast.error(`Erreur lors du changement de statut: ${error.message}`);
    } finally {
      setChangingStatus(null);
    }
  };

  // Get customer name
  const getCustomerName = (exchange) => {
    if (!exchange.bonCommande) return '-';

    const firstName = exchange.bonCommande.clientFirstName || '';
    const lastName = exchange.bonCommande.clientLastName || '';

    if (!firstName && !lastName) return 'Client non spécifié';
    return `${firstName} ${lastName}`.trim();
  };

  // Handle delete action
  const handleDelete = (exchangeId) => {
    setExchangeToDelete(exchangeId);
    setDeleteModalOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    try {
      await deleteExchange(exchangeToDelete);
      toast.success("Échange supprimé avec succès");
      setDeleteModalOpen(false);
      setExchangeToDelete(null);

      // Refresh data after deletion
      if (refreshData) {
        refreshData();
      }

      // Refresh dashboard counts if available
      if (refreshDashboard) {
        refreshDashboard();
      }
    } catch (error) {
      toast.error(`Erreur lors de la suppression: ${error.message}`);
    }
  };

  // Handle duplicate action
  const handleDuplicate = (exchange) => {
    console.log('Duplicate exchange', exchange.id);
    // Implement duplicate functionality
  };

  // Handle refresh action
  const handleRefresh = (exchangeId) => {
    console.log('Refresh exchange status', exchangeId);
    // Implement refresh functionality
    if (refreshData) {
      refreshData();
    }
  };

  return (
    <div className="flex flex-col">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#174540]"></div>
              </div>
            ) : !exchanges || exchanges.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                Aucun échange trouvé
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {selectedExchanges && handleExchangeSelection && (
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                            onChange={(e) => {
                              // Implement select all functionality if needed
                            }}
                          />
                        </th>
                      )}
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Référence
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commande origine
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {exchanges.map((exchange) => (
                      <tr key={exchange.id} className="hover:bg-gray-50">
                        {selectedExchanges && handleExchangeSelection && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                              checked={selectedExchanges.some(item => item.id === exchange.id)}
                              onChange={() => handleExchangeSelection(exchange.id, exchange.etat)}
                            />
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{exchange.reference}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{formatDate(exchange.dateEchange)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{getCustomerName(exchange)}</div>
                          <div className="text-sm text-gray-500">{exchange.bonCommande?.clientEmail || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{exchange.bonCommande?.reference || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{exchange.totalTTC?.toFixed(2)} DT</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">

                            {/* Delete button */}
                            <button
                            disabled={!hasPermission('delete')}
                              onClick={() => handleDelete(exchange.id)}
                              className="text-red-600 hover:text-red-900  disabled:text-gray-700  disabled:cursor-not-allowed"
                              title="Supprimer"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {pagination && (
                  <Pagination
                    currentPage={pagination.currentPage || pagination.page || 1}
                    totalItems={pagination.totalItems || pagination.total || 0}
                    pageSize={pagination.pageSize || 10}
                    paginate={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Premium Delete Confirmation Modal */}
      {deleteModalOpen && (
        <Modal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title="Confirmation de suppression"
          submitLabel="Supprimer"
          cancelLabel="Annuler"
          onSubmit={confirmDelete}
          submitColor="red"
          size="md"
        >
          <div className="py-2">
            <div className="rounded-lg bg-red-50 p-4 border border-red-100 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Action irréversible</h3>
                  <div className="mt-1 text-sm text-red-700">
                    Cette action ne peut pas être annulée. Toutes les données associées à cet échange seront définitivement supprimées.
                  </div>
                </div>
              </div>
            </div>

            <p className="text-gray-700 text-base">
              Vous êtes sur le point de supprimer l'échange <span className="font-semibold">#{exchangeToDelete}</span>.
              Êtes-vous certain de vouloir procéder à cette suppression?
            </p>

            <div className="mt-4 text-sm text-gray-500 bg-gray-100 rounded-md p-3 border border-gray-200">
              <p className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Conseil: Vérifiez que toutes les informations importantes ont été sauvegardées avant de supprimer.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ExchangesList;